import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchBookings, type Booking } from "@/lib/bookings";
import { deleteBlock, fetchBlocks, type AvailabilityBlock } from "@/lib/availability";
import { toISODate } from "@/pages/admin/format";
import CalendarToolbar, {
  type CalendarView,
} from "@/pages/admin/calendar/components/CalendarToolbar";
import MonthView from "@/pages/admin/calendar/components/MonthView";
import WeekView from "@/pages/admin/calendar/components/WeekView";
import DayView from "@/pages/admin/calendar/components/DayView";
import DayAgenda from "@/pages/admin/calendar/components/DayAgenda";
import BlockDateModal from "@/pages/admin/calendar/components/BlockDateModal";
import {
  CALENDAR_STATUS_META,
  addDays,
  addMonths,
  addWeeks,
  buildDayInfos,
  buildMonthGrid,
  buildWeek,
  formatMonthTitle,
  formatWeekTitle,
  type CalendarStatus,
} from "@/pages/admin/calendar/calendarUtils";

const LEGEND: CalendarStatus[] = ["available", "limited", "booked", "fully", "blocked", "maintenance"];

export default function AdminCalendar() {
  const [view, setView] = useState<CalendarView>("month");
  const [cursor, setCursor] = useState(() => new Date());
  const [selectedIso, setSelectedIso] = useState(() => toISODate(new Date()));
  const [statusFilter, setStatusFilter] = useState("all");
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [blocks, setBlocks] = useState<AvailabilityBlock[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [blockDefaultDate, setBlockDefaultDate] = useState<string | undefined>(undefined);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [bookingData, blockData] = await Promise.all([fetchBookings(), fetchBlocks()]);
      setBookings(bookingData);
      setBlocks(blockData);
    } catch (err) {
      setError(
        err instanceof Error && err.message
          ? err.message
          : "We couldn't load the calendar. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filteredBookings = useMemo(
    () => (statusFilter === "all" ? bookings : bookings.filter((booking) => booking.status === statusFilter)),
    [bookings, statusFilter],
  );

  const days = useMemo(() => {
    if (view === "month") return buildMonthGrid(cursor);
    if (view === "week") return buildWeek(cursor);
    return [cursor];
  }, [view, cursor]);

  const dayInfos = useMemo(
    () => buildDayInfos(days, filteredBookings, blocks),
    [days, filteredBookings, blocks],
  );

  const selectedDay = dayInfos.find((day) => day.iso === selectedIso) || null;

  const title =
    view === "month"
      ? formatMonthTitle(cursor)
      : view === "week"
        ? formatWeekTitle(cursor)
        : cursor.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  const goPrev = () => setCursor((value) => (view === "month" ? addMonths(value, -1) : view === "week" ? addWeeks(value, -1) : addDays(value, -1)));
  const goNext = () => setCursor((value) => (view === "month" ? addMonths(value, 1) : view === "week" ? addWeeks(value, 1) : addDays(value, 1)));
  const goToday = () => {
    const now = new Date();
    setCursor(now);
    setSelectedIso(toISODate(now));
  };

  const openBlockModal = (iso?: string) => {
    setBlockDefaultDate(iso || selectedIso || undefined);
    setModalOpen(true);
  };

  const handleRemoveBlock = async (block: AvailabilityBlock) => {
    try {
      await deleteBlock(block.id);
      setBlocks((list) => list.filter((item) => item.id !== block.id));
    } catch {
      setError("We couldn't remove that block. Please try again.");
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6 md:mb-8">
        <div>
          <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-accent-700">
            Operations
          </span>
          <h1 className="font-heading text-3xl md:text-4xl text-foreground-950 font-semibold mt-2">
            Booking calendar
          </h1>
          <p className="text-sm md:text-base text-foreground-600 mt-2 max-w-2xl">
            See every booking by date and block availability for maintenance, holidays or private events.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-2 border border-background-300 text-foreground-800 text-sm font-semibold px-4 py-2.5 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className={`ri-refresh-line ${loading ? "animate-spin" : ""}`}></i>
            Refresh
          </button>
          <button
            type="button"
            onClick={() => openBlockModal(undefined)}
            className="inline-flex items-center gap-2 bg-primary-500 text-background-50 text-sm font-semibold px-4 py-2.5 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-calendar-close-line"></i>
            Block dates
          </button>
        </div>
      </div>

      {error ? (
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 bg-accent-100 border border-accent-300 rounded-lg px-4 py-3.5 mb-5">
          <p className="flex items-start gap-2 text-sm text-accent-900 flex-1">
            <i className="ri-error-warning-line text-lg"></i>
            <span>{error}</span>
          </p>
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-2 bg-accent-500 text-background-50 text-sm font-semibold px-4 py-2.5 rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer self-start"
          >
            <i className="ri-refresh-line"></i> Retry
          </button>
        </div>
      ) : null}

      <CalendarToolbar
        view={view}
        onViewChange={setView}
        title={title}
        onPrev={goPrev}
        onNext={goNext}
        onToday={goToday}
        statusFilter={statusFilter}
        onStatusFilter={setStatusFilter}
      />

      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-4">
        {LEGEND.map((status) => (
          <span key={status} className="inline-flex items-center gap-2 text-xs text-foreground-600">
            <span className={`w-2.5 h-2.5 rounded-full ${CALENDAR_STATUS_META[status].dot}`}></span>
            {CALENDAR_STATUS_META[status].label}
          </span>
        ))}
      </div>

      <div className="mt-5">
        {loading ? (
          <div className="bg-background-50 border border-background-200 rounded-lg py-20 flex flex-col items-center gap-3">
            <i className="ri-loader-4-line text-3xl animate-spin text-primary-500"></i>
            <p className="text-sm text-foreground-600">Loading the calendar…</p>
          </div>
        ) : view === "month" ? (
          <div className="grid grid-cols-1 xl:grid-cols-4 gap-4">
            <div className="xl:col-span-3">
              <MonthView days={dayInfos} cursor={cursor} selectedIso={selectedIso} onSelect={setSelectedIso} />
            </div>
            <DayAgenda day={selectedDay} onBlock={openBlockModal} onRemoveBlock={handleRemoveBlock} />
          </div>
        ) : view === "week" ? (
          <WeekView days={dayInfos} selectedIso={selectedIso} onSelect={setSelectedIso} />
        ) : (
          <DayView day={dayInfos[0]} onBlock={openBlockModal} onRemoveBlock={handleRemoveBlock} />
        )}
      </div>

      <BlockDateModal
        open={modalOpen}
        defaultDate={blockDefaultDate}
        onClose={() => setModalOpen(false)}
        onCreated={(block) => setBlocks((list) => [...list, block])}
      />
    </div>
  );
}